import { Component, DestroyRef, ElementRef, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, debounceTime } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatMenuModule } from '@angular/material/menu';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../_services/alert.service';
import {
  PurchaseOrderService,
  PurchaseOrderListItem,
  PurchaseOrderSummary,
} from '../../_services/purchase-order.service';
import { purchaseOrderStatus, purchaseOrderBillingStatus } from '../../../Shared/_constants/constants';
import { currencySymbol } from './currencies';
import { purchaseStatusClass, billingStatusClass } from './status-styles';
import { saveListQuery } from './list-state';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';

// Server-paginated (see PurchaseOrderService / purchase-orders.service.ts
// in nustone-backend) - `orders` is always just the current page's rows,
// never the whole table. One search box feeds the backend's single `search`
// term, which matches reference, supplier name and the supplier's document no.
@Component({
    selector: 'app-purchases',
    imports: [PaginatorPagesDirective,
        MainLayoutComponent,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatPaginatorModule,
        MatMenuModule,
        RouterLink,
        DatePipe,
        FormsModule,
        CommonModule,
    ],
    templateUrl: './purchases.component.html',
    styleUrl: './purchases.component.css'
})
export class PurchasesComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private search$ = new Subject<void>();

  loading = true;
  loadError = false;

  orders: PurchaseOrderListItem[] = [];
  count = 0;
  pageNumber = 0;
  itemsPerPage = 10;

  statusTabs = ['ALL', ...Object.values(purchaseOrderStatus)];
  billingOptions = ['ALL', ...Object.values(purchaseOrderBillingStatus)];
  status = 'ALL';
  billingStatus = 'ALL';
  lateOnly = false;
  search = '';

  statusCounts: Record<string, number> = {};
  summary: PurchaseOrderSummary = { open: 0, late: 0, awaitingPayment: 0, overdue: 0 };

  currencySymbol = currencySymbol;
  getStatusClass = purchaseStatusClass;
  getBillingStatusClass = billingStatusClass;
  readonly skeletonRows = [1, 2, 3, 4, 5, 6];

  @ViewChild('searchBox') searchBox?: ElementRef<HTMLInputElement>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private purchaseOrderService: PurchaseOrderService,
  ) {}

  ngOnInit(): void {
    // Search as you type; Enter searches straight away.
    this.search$.pipe(debounceTime(350), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.applyFilters());
    this.readUrl();
    this.loadPurchaseOrders();
  }

  // Filters and page live in the URL, so coming back from a PO (or sharing
  // the link) lands on the same view instead of resetting to page one.
  private readUrl(): void {
    const q = this.route.snapshot.queryParamMap;
    this.search = q.get('q') ?? '';
    const status = q.get('status');
    if (status && this.statusTabs.includes(status)) this.status = status;
    const billing = q.get('billing');
    if (billing && this.billingOptions.includes(billing)) this.billingStatus = billing;
    this.lateOnly = q.get('late') === '1';
    const page = Number(q.get('page'));
    if (page > 1) this.pageNumber = page - 1;
    const size = Number(q.get('size'));
    if ([10, 20, 50].includes(size)) this.itemsPerPage = size;
  }

  private writeUrl(): void {
    const queryParams = {
      q: this.search.trim() || null,
      status: this.status !== 'ALL' ? this.status : null,
      billing: this.billingStatus !== 'ALL' ? this.billingStatus : null,
      late: this.lateOnly ? '1' : null,
      page: this.pageNumber > 0 ? this.pageNumber + 1 : null,
      size: this.itemsPerPage !== 10 ? this.itemsPerPage : null,
    };
    saveListQuery(queryParams);
    this.router.navigate([], { relativeTo: this.route, replaceUrl: true, queryParams });
  }

  // "/" jumps to the search box, like most list pages on the web.
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== '/' || event.metaKey || event.ctrlKey) return;
    const target = event.target as HTMLElement | null;
    if (target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)) return;
    event.preventDefault();
    this.searchBox?.nativeElement.focus();
  }

  loadPurchaseOrders(): void {
    this.writeUrl();
    this.loading = true;
    this.loadError = false;
    this.purchaseOrderService
      .getPurchaseOrders({
        pageIndex: this.pageNumber,
        pageSize: this.itemsPerPage,
        search: this.search.trim() || undefined,
        status: this.status,
        billingStatus: this.billingStatus,
        lateOnly: this.lateOnly || undefined,
      })
      .subscribe({
        next: (response) => {
          this.orders = response.items;
          this.count = response.count;
          this.statusCounts = response.statusCounts ?? {};
          this.summary = response.summary ?? this.summary;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.loadError = true;
          this.alertService.alert('Could not load purchase orders', 'error');
        },
      });
  }

  onSearchInput(): void {
    this.search$.next();
  }

  clearSearch(): void {
    this.search = '';
    this.applyFilters();
  }

  applyFilters(): void {
    this.pageNumber = 0;
    this.loadPurchaseOrders();
  }

  setStatus(status: string): void {
    this.status = status;
    // "Late" only ever means orders still in flight, so a status tab that can't be late clears it.
    if (this.lateOnly && !['ALL', 'ORDERED', 'PARTIALLY RECEIVED'].includes(status)) this.lateOnly = false;
    this.applyFilters();
  }

  setBilling(billing: string): void {
    this.billingStatus = billing;
    this.applyFilters();
  }

  // Header figures double as shortcuts to the orders they count.
  showLate(): void {
    this.reset(false);
    this.lateOnly = true;
    this.applyFilters();
  }

  showAwaitingPayment(): void {
    this.reset(false);
    this.billingStatus = purchaseOrderBillingStatus.BILLED;
    this.applyFilters();
  }

  showOverdue(): void {
    this.reset(false);
    this.billingStatus = purchaseOrderBillingStatus.OVERDUE;
    this.applyFilters();
  }

  private reset(load: boolean): void {
    this.status = 'ALL';
    this.billingStatus = 'ALL';
    this.lateOnly = false;
    this.search = '';
    if (load) this.applyFilters();
  }

  onResetFilters(): void {
    this.reset(true);
  }

  get filtersActive(): boolean {
    return !!this.search.trim() || this.status !== 'ALL' || this.billingStatus !== 'ALL' || this.lateOnly;
  }

  tabCount(tab: string): number {
    if (tab === 'ALL') return Object.values(this.statusCounts).reduce((a, b) => a + b, 0);
    return this.statusCounts[tab] ?? 0;
  }

  updateList(event: PageEvent): void {
    if (this.itemsPerPage !== event.pageSize) {
      this.itemsPerPage = event.pageSize || 10;
      this.pageNumber = 0;
    } else {
      this.pageNumber = event.pageIndex;
    }
    this.loadPurchaseOrders();
  }

  getSupplierName(po: PurchaseOrderListItem): string {
    return po.supplier?.name || 'Unknown supplier';
  }

  // Whole days between today and the expected date (negative = still to come).
  // Only meaningful while stock is still due to arrive.
  daysLate(po: PurchaseOrderListItem): number | null {
    if (!po.expectedDeliveryDate) return null;
    if (![purchaseOrderStatus.ORDERED, purchaseOrderStatus.PARTIALLY_RECEIVED].includes(po.status as purchaseOrderStatus)) return null;
    const expected = new Date(po.expectedDeliveryDate);
    const today = new Date();
    const a = Date.UTC(expected.getFullYear(), expected.getMonth(), expected.getDate());
    const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((b - a) / 86_400_000);
  }

  receivedPct(po: PurchaseOrderListItem): number {
    if (!po.itemCount) return 0;
    return Math.max(0, Math.min(100, Math.round((po.receivedCount / po.itemCount) * 100)));
  }

  onViewSupplier(po: PurchaseOrderListItem, event?: Event): void {
    event?.stopPropagation();
    if (po.supplierId) this.router.navigate(['/suppliers/edit', po.supplierId]);
  }

  // Dot colour for a status, matching the badge palette in status-styles.
  statusDot(status: string): string {
    switch (status) {
      case purchaseOrderStatus.RECEIVED: return 'bg-green-500';
      case purchaseOrderStatus.PARTIALLY_RECEIVED: return 'bg-orange-400';
      case purchaseOrderStatus.ORDERED: return 'bg-blue-500';
      case purchaseOrderStatus.CANCELLED: return 'bg-red-500';
      case purchaseOrderStatus.DRAFT: return 'bg-neutral-400';
      default: return 'bg-gray-300';
    }
  }

  supplierInitials(po: PurchaseOrderListItem): string {
    const words = (po.supplier?.name || '?').trim().split(/\s+/);
    return ((words[0]?.[0] ?? '?') + (words[1]?.[0] ?? '')).toUpperCase();
  }

  // Days until the expected delivery (negative once it's passed), while
  // stock is still due.
  daysUntilDue(po: PurchaseOrderListItem): number | null {
    const late = this.daysLate(po);
    return late === null ? null : -late;
  }

  onNewForSupplier(po: PurchaseOrderListItem): void {
    if (po.supplierId) this.router.navigate(['/purchases/create'], { queryParams: { supplierId: po.supplierId } });
  }

  onCreatePurchaseOrder(): void {
    this.router.navigate(['/purchases/create']);
  }

  onView(po: PurchaseOrderListItem): void {
    this.router.navigate(['/purchases/edit', po.id]);
  }
}
